// ytreSearchHighlight.js — Feb 17 2026 (Randy pt-ytre-canvas-search).
//
// Small helpers shared by the YTRE canvas search feature and the card
// bodies that render highlighted text.
//
// Scope: plain-substring, case-insensitive matching (no regex, no
// fuzzy). Returns match ranges and produces a React fragment with
// <mark> wrappers around each match, with the active match tinted
// distinctly so the user can see where the search cursor landed.
//
// Randy's search spec (Feb 17, 2026):
//   Q1: search everything on canvas regardless of parent state
//   Q2: only currently-displayed transcript body (post-recapture text)
//   Q3: plain substring, case-insensitive (no regex, no fuzzy)
//   a:  match in a collapsed card → expand JUST that child to stage-1,
//       parent stays as-is
//   b:  steady persistent highlight on the active match (not a pulse)
//   c:  desktop: Enter=next, Shift+Enter=prev, ▲/▼ also work.
//       mobile: Enter cycles forward, wrapping N/N → 1/N. On-screen
//       ▲ button still works for explicit back.

import React from "react";

/**
 * Find every case-insensitive substring occurrence of `query` in `text`.
 * Returns an array of `{ start, end }` in text-index order.
 *
 * Empty query or empty text → returns [].
 * Empty match runs are guarded (shouldn't occur but safety belt).
 */
export function computeMatches(text, query) {
  if (!text || !query) return [];
  const q = String(query);
  if (!q.length) return [];
  const src = String(text);
  const srcLower = src.toLowerCase();
  const qLower = q.toLowerCase();
  const ranges = [];
  let from = 0;
  while (from <= srcLower.length - qLower.length) {
    const idx = srcLower.indexOf(qLower, from);
    if (idx < 0) break;
    ranges.push({ start: idx, end: idx + qLower.length });
    from = idx + qLower.length; // no overlapping matches
  }
  return ranges;
}

/**
 * Render `text` with <mark> wrappers around every match of `query`.
 * The occurrence at `activeIdx` (0-based, within this text) gets a
 * stronger tint so the user knows where the search cursor is.
 *
 * Returns a React fragment (an array of strings + <mark> elements).
 * If `query` is empty or no matches exist, returns `text` unchanged.
 *
 * options:
 *   activeIdx     number | null — occurrence index within this text
 *                                 that is the currently-active match
 *   entryId       string        — used to disambiguate <mark> keys
 *                                 across cards (React key uniqueness)
 *   activeTestId  string        — data-testid slapped on the active
 *                                 <mark> so tests can locate it.
 */
export function renderWithMarks(text, query, options = {}) {
  const { activeIdx = null, entryId = "", activeTestId = "ytre-canvas-search-active-mark" } = options;
  if (!text || !query) return text;
  const ranges = computeMatches(text, query);
  if (!ranges.length) return text;
  const parts = [];
  let cursor = 0;
  ranges.forEach((r, i) => {
    if (r.start > cursor) {
      parts.push(text.slice(cursor, r.start));
    }
    const isActive = activeIdx != null && i === activeIdx;
    parts.push(
      <mark
        key={`mk-${entryId}-${i}`}
        data-testid={isActive
          ? activeTestId
          : `ytre-canvas-search-mark-${entryId}-${i}`}
        className={isActive
          ? "bg-[#F0B450] text-[#1C1B1A] rounded-[2px] px-[1px]"
          : "bg-[#F4E4A3] text-[#1C1B1A] rounded-[2px] px-[1px]"}
      >
        {text.slice(r.start, r.end)}
      </mark>
    );
    cursor = r.end;
  });
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return parts;
}

/**
 * Return the searchable text for a YTRE entry. Prefers the currently-
 * displayed body (transcript_revised if set for silo_response, else
 * transcript_raw). Also includes the author display name so searching
 * by "@markandpamtaylor" jumps to the right card.
 *
 * Returned value is a single string with the display-name concatenated
 * to the body via a newline — matches within either segment are found
 * naturally by computeMatches.
 */
export function searchableTextOf(entry) {
  if (!entry) return "";
  const kind = entry.kind;
  let body = "";
  if (kind === "silo_response") {
    body = entry.silo_response_text || "";
  } else if (kind === "transcript") {
    // Prefer the currently-displayed transcript body. If the user
    // toggled Revised view, showRevised in the card picks
    // transcript_revised — but for search purposes we search whichever
    // is present (raw is always there).
    body = entry.transcript_revised || entry.transcript_raw || "";
  } else {
    // user_comment — the body is always transcript_raw (revised is
    // used by other flows).
    body = entry.transcript_raw || "";
  }
  const author = entry.author_display_name || "";
  return author ? `${author}\n${body}` : body;
}

/**
 * Compute all matches across every entry on the canvas, in the same
 * visual order as `entries`. Returns a flat list of match records:
 *   [{ entryId, occIdx, start, end }, ...]
 * `occIdx` is the occurrence index within that entry's text (0-based).
 *
 * Used by the parent search state to build the cycle-through list.
 */
export function computeCanvasMatches(entries, query) {
  if (!entries || !entries.length || !query) return [];
  const out = [];
  for (const entry of entries) {
    const text = searchableTextOf(entry);
    const ranges = computeMatches(text, query);
    ranges.forEach((r, i) => {
      out.push({ entryId: entry.id, occIdx: i, start: r.start, end: r.end });
    });
  }
  return out;
}
